import { Sheet, LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useApiService } from "../../services/apiService";

export function ProductExcelDownload({ entityId }: { entityId: number }) {
  const api = useApiService();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = useRef<AbortController | null>(null);
  useEffect(() => {
    setPending(false); setError(null);
    return () => { active.current?.abort(); active.current = null; };
  }, [entityId]);

  const download = async () => {
    if (active.current || !Number.isSafeInteger(entityId) || entityId <= 0) return;
    const controller = new AbortController();
    active.current = controller;
    setPending(true); setError(null);
    try {
      const blob = await api.getForBlob(`/product-search/products/${entityId}/export.xlsx`, { signal: controller.signal }, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
      if (controller.signal.aborted) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      try {
        anchor.href = url; anchor.download = `product-${entityId}.xlsx`;
        document.body.appendChild(anchor); anchor.click();
      } finally {
        anchor.remove();
        // Allow the browser to consume the object URL before releasing it.
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch {
      if (!controller.signal.aborted) setError("Excel download failed. Please try again.");
    } finally {
      if (active.current === controller) active.current = null;
      if (!controller.signal.aborted) setPending(false);
    }
  };

  return <span className="ps-specification-download">
    <button type="button" className="ps-action-secondary ps-action-icon" aria-label="Download product Excel" title={pending ? "Generating Excel…" : "Download product Excel"}
      aria-busy={pending} disabled={pending || !Number.isSafeInteger(entityId) || entityId <= 0} onClick={() => void download()}>
      {pending ? <LoaderCircle className="ps-download-spinner" aria-hidden="true" /> : <Sheet aria-hidden="true" />}
    </button>
    {pending && <span className="sr-only" role="status">Generating product Excel</span>}
    {error && <span className="ps-download-error" role="alert">{error} <button type="button" onClick={() => void download()}>Retry</button></span>}
  </span>;
}

